import { EmptyState } from '@/components/page-header';
import { DocumentForm } from '@/components/sales/document-form';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { useSalesDocument, type SalesKind } from '@/lib/sales';
import { useCompanySettings } from '@/lib/settings';

/** Route body for "new" and "view/edit" of a sales document. */
export function DocumentPage({
  kind,
  id,
  copyOf,
  quoteId,
  fromId,
  contactId,
  openApply,
  openSend,
}: {
  kind: SalesKind;
  openApply?: boolean;
  openSend?: boolean;
  id?: string;
  copyOf?: string;
  quoteId?: string;
  /** A sales order or challan whose open lines this invoice or challan takes (document 06 S14). */
  fromId?: string;
  contactId?: string;
}) {
  const orgId = useOrgId();
  const doc = useSalesDocument(id);
  const source = useSalesDocument(copyOf ?? quoteId ?? fromId);
  const settings = useCompanySettings();
  const t = useT();
  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  if (
    (id && doc.isPending) ||
    ((copyOf || quoteId || fromId) && source.isPending) ||
    settings.isPending
  )
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (id && doc.isError)
    return <EmptyState title={t('salesPages.document.notFound')} hint={doc.error.message} />;
  if (id && doc.data && doc.data.kind !== kind)
    return (
      <EmptyState
        title={t('salesPages.document.notFound')}
        hint={t('salesPages.document.otherKind')}
      />
    );
  return (
    <DocumentForm
      key={`${orgId}-${id ?? 'new'}-${doc.data?.version ?? 0}-${copyOf ?? ''}-${quoteId ?? ''}-${fromId ?? ''}`}
      kind={kind}
      doc={doc.data ?? null}
      source={source.data ?? null}
      {...(quoteId ? { quoteId } : {})}
      {...(fromId ? { fromOrder: true } : {})}
      {...(contactId ? { defaultContactId: contactId } : {})}
      {...(openApply ? { openApply } : {})}
      {...(openSend ? { openSend } : {})}
    />
  );
}

export type NewDocSearch = {
  copyOf?: string;
  quoteId?: string;
  fromId?: string;
  contactId?: string;
};

export function validateNewDocSearch(s: Record<string, unknown>): NewDocSearch {
  const out: NewDocSearch = {};
  for (const k of ['copyOf', 'quoteId', 'fromId', 'contactId'] as const)
    if (typeof s[k] === 'string') out[k] = s[k] as string;
  return out;
}

export type ViewDocSearch = { send?: string; apply?: string };

export function validateViewDocSearch(s: Record<string, unknown>): ViewDocSearch {
  const out: ViewDocSearch = {};
  for (const k of ['send', 'apply'] as const) if (typeof s[k] === 'string') out[k] = s[k] as string;
  return out;
}
